import React, {useState} from 'react';
import MultiStatesButton from "./MultiStatesButton";
import classNames from "classnames";

const SIZE_OPTIONS: { value: PhotoSize, label: string, iconClass: string }[] = [
    {value: "klein", label: "Klein", iconClass: "w-1.5 h-1.5"},
    {value: "mittel", label: "Mittel", iconClass: "w-2.5 h-2.5"},
    {value: "gross", label: "Groß", iconClass: "w-3.5 h-3.5"},
]

type Pros = {
    label?: string,
    photos: PhotoToCreate[],
    onDelete?: (photo: PhotoToCreate) => void,
    onFileSelect?: (file: File, onFinished: () => void) => void;
    fileUploadRef: any,
    sizeSelectable?: boolean,
    onSizeChange?: (photo: PhotoToCreate, size: PhotoSize) => void,
}

function PhotosEditor({label, photos, onDelete, onFileSelect, fileUploadRef, sizeSelectable, onSizeChange}: Pros) {

    const [uploading, setUploading] = useState<boolean>(false);
    const [fileSelected, setFileSelected] = useState<File | null>();
    const [imagesLoaded, setImagesLoaded] = useState<{
        [id: string]: boolean
    }>({});

    const markLoaded = (photoId: string) => {
        setImagesLoaded(prev => prev[photoId] ? prev : {
            ...prev,
            [photoId]: true
        });
    }

    const handleDelete = (photo: PhotoToCreate) => {
        if (!uploading && window.confirm("Bestätigen, um es zu löschen?")) {
            onDelete && onDelete(photo);
        }
    }

    const handleFileSelect = (files: FileList | null) => {
        if (files && files.length > 0) {
            setFileSelected(files[0]);
        }
    }

    const handleSave = () => {
        if (onFileSelect && fileSelected) {
            setUploading(true);
            onFileSelect(fileSelected, () => {
                setUploading(false);
                setFileSelected(null);
            });
        }
    }

    return (
        <div className="max-w-sm mx-auto">
            <label htmlFor="buttons" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                {label ?? "Fotos"}
            </label>
            <div className="max-w-sm mx-auto">
                <input
                    ref={fileUploadRef}
                    className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 dark:text-gray-400 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400"
                    type="file"
                    accept="image/jpeg, image/png"
                    onChange={(e) => handleFileSelect(e.target.files)}
                    disabled={uploading}
                />
                <div className="mt-1 text-sm text-gray-500 dark:text-gray-300" id="user_avatar_help">
                    <div className="flex items-center">
                        (jpg, jpeg, png, maximal 5MB)
                    </div>
                </div>
                {
                    fileSelected && (
                        <div className="mt-2">
                            <MultiStatesButton
                                disabled={false}
                                color="blue"
                                action={handleSave}
                                normalText="Speichern"
                                loadingText="Foto speichern..."
                                loading={uploading}
                            />
                        </div>
                    )
                }
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                {
                    photos && photos.map((photo, index) => (
                        <div
                            key={index}
                            className="relative"
                        >
                            <img
                                ref={(el) => {
                                    // Handles images already cached/complete by mount time,
                                    // since onLoad won't fire again for those.
                                    if (el && el.complete) {
                                        markLoaded(photo.id);
                                    }
                                }}
                                className={classNames({
                                    "h-auto max-w-full rounded-lg": true,
                                    "hidden": !imagesLoaded[photo.id]
                                })}
                                src={photo.url}
                                alt={photo.title || ""}
                                onLoad={() => markLoaded(photo.id)}
                            />
                            {
                                imagesLoaded[photo.id] && (
                                    <button
                                        type="button"
                                        onClick={() => handleDelete(photo)}
                                        title="Löschen"
                                        aria-label="Foto löschen"
                                        className="absolute top-1 right-1 z-10 flex items-center justify-center w-6 h-6 rounded-full bg-black/60 text-white hover:bg-red-600 transition-colors"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none"
                                             viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
                                        </svg>
                                    </button>
                                )
                            }
                            <svg aria-hidden="true"
                                 className={classNames({
                                     "w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600": true,
                                     "hidden": imagesLoaded[photo.id]
                                 })}
                                 viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                    fill="currentColor"/>
                                <path
                                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                    fill="currentFill"/>
                            </svg>
                            {
                                sizeSelectable && onSizeChange && (
                                    <div className="flex justify-center items-center gap-1.5 mt-1.5">
                                        {
                                            SIZE_OPTIONS.map(opt => (
                                                <button
                                                    type="button"
                                                    key={opt.value}
                                                    title={opt.label}
                                                    aria-label={opt.label}
                                                    onClick={() => onSizeChange(photo, opt.value)}
                                                    className={classNames("flex-none flex items-center justify-center w-6 h-6 rounded border", {
                                                        "bg-blue-600 border-blue-600": (photo.size ?? "mittel") === opt.value,
                                                        "bg-gray-50 border-gray-300": (photo.size ?? "mittel") !== opt.value,
                                                    })}
                                                >
                                                    <span className={classNames("rounded-sm", opt.iconClass, {
                                                        "bg-white": (photo.size ?? "mittel") === opt.value,
                                                        "bg-gray-400": (photo.size ?? "mittel") !== opt.value,
                                                    })}/>
                                                </button>
                                            ))
                                        }
                                    </div>
                                )
                            }
                        </div>
                    ))
                }
            </div>
        </div>

    );
}

export default PhotosEditor;
